// Artisan function types that have been retired. They're filtered out of
// every dropdown, tab list, and badge display. Partners that still carry
// these types in Firestore are not affected — the type is just hidden
// from the UI so users stop selecting it for new partners.
export const HIDDEN_COMPANY_TYPES = new Set([
  "enduit",
  "assainissement",
  "photovoltaique",
  "cuisine",
  "livraison",
  "etude_thermique",
  "parquet",
  "bureau_d_etudes",
  "bureau_d_etudes_thermique",
  "controle_pmr",
  "architecte",
]);

// Declared in display order. Every consumer (PartnersSection,
// CreateProjectModal, dropdowns, tables, …) must preserve this order.
// Do not re-sort alphabetically.
export const COMPANY_TYPE_OPTIONS = [
  { value: "terrassement", label: "Terrassement" },
  { value: "gros_oeuvre", label: "Gros œuvre" },
  { value: "traitement_termite", label: "Traitement termites" },
  { value: "charpente", label: "Charpente" },
  { value: "couverture", label: "Couverture" },
  { value: "etancheite", label: "Étanchéité" },
  { value: "menuiserie", label: "Menuiserie" },
  { value: "ravalement", label: "Ravalement" },
  { value: "gouttieres", label: "Gouttières" },
  { value: "cloisonnement", label: "Plaquisterie" },
  { value: "escalier", label: "Escalier" },
  { value: "isolation", label: "Isolation projetée" },
  { value: "electricite", label: "Électricité" },
  { value: "plomberie", label: "Plomberie" },
  { value: "chauffage", label: "Chauffage" },
  { value: "chape", label: "Chape" },
  { value: "carrelage", label: "Carrelage" },
  { value: "peinture", label: "Peinture" },
  { value: "nettoyage", label: "Nettoyage" },
  { value: "controle_re2020", label: "Contrôle RE2020" },
  { value: "autre", label: "Autre" },
];

// Extra partner categories that exist ONLY in the project create/modify
// partner pickers. They are deliberately kept OUT of COMPANY_TYPE_OPTIONS so
// they never leak into Charge artisan, the Artisans page, partner type
// pickers, planning grids or email templates. Two of them (etude_thermique,
// architecte) also live in HIDDEN_COMPANY_TYPES and stay hidden everywhere
// except the two project modals, which render PROJECT_PARTNER_CATEGORY_OPTIONS.
//
// Keyed by the COMPANY_TYPE_OPTIONS value each block should appear AFTER, so
// the canonical insertion order lives in exactly one place.
const PROJECT_EXTRA_CATEGORIES_AFTER: Record<
  string,
  { value: string; label: string }[]
> = {
  nettoyage: [
    { value: "paysagiste", label: "Paysagiste" },
    { value: "pisciniste", label: "Pisciniste" },
  ],
  controle_re2020: [
    { value: "etude_beton", label: "Étude béton" },
    { value: "etude_thermique", label: "Étude thermique" },
    { value: "dommage_ouvrage", label: "Dommage ouvrage" },
    { value: "architecte", label: "Architecte" },
  ],
};

// The single ordered category list shown in BOTH the Create and Modify project
// modals (and their add-partner picker). This is what "unifies" the two modals:
// they used to derive categories from different sources (a fixed taxonomy vs.
// the org's planning-template sections). Now both render this list.
export const PROJECT_PARTNER_CATEGORY_OPTIONS = COMPANY_TYPE_OPTIONS.flatMap(
  (o) => [o, ...(PROJECT_EXTRA_CATEGORIES_AFTER[o.value] || [])],
);

// Built from the project-modal list so the extra categories' labels also
// resolve in badges/displays. Adding a label here does NOT add the type to any
// picker — only PROJECT_PARTNER_CATEGORY_OPTIONS / COMPANY_TYPE_OPTIONS do that.
export const COMPANY_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  PROJECT_PARTNER_CATEGORY_OPTIONS.map((o) => [o.value, o.label]),
);

// Virtual "expansions": some categories include partners tagged under
// other, narrower categories. Used to decide which partners surface in a
// given section/column.
export const COMPANY_TYPE_EXPANSIONS: Record<string, string[]> = {
  chauffage: ["plomberie"],
  terrassement: ["gros_oeuvre"],
};

/**
 * True if `partner` belongs in the section for `sectionType`.
 *
 * Honours virtual expansions, so this is used when picking which org partners
 * to OFFER for a section (e.g. a `gros_oeuvre` partner is offered under
 * Terrassement). Once a partner is actually added to a project we materialize
 * those expansions into real `company_types` (see `addSectionToCompanyTypes`)
 * and display added partners with a plain `company_types.includes(section)`,
 * so each section can remove its own tag independently.
 */
export function partnerMatchesType(
  partnerTypes: string[] | undefined,
  sectionType: string,
): boolean {
  if (!partnerTypes) return false;
  if (partnerTypes.includes(sectionType)) return true;
  const extra = COMPANY_TYPE_EXPANSIONS[sectionType] || [];
  return extra.some((t) => partnerTypes.includes(t));
}

// Reverse of COMPANY_TYPE_EXPANSIONS: maps a "source" type to the sections that
// absorb it. e.g. gros_oeuvre -> ["terrassement"], plomberie -> ["chauffage"].
const REVERSE_COMPANY_TYPE_EXPANSIONS: Record<string, string[]> = (() => {
  const map: Record<string, string[]> = {};
  for (const [section, sources] of Object.entries(COMPANY_TYPE_EXPANSIONS)) {
    for (const source of sources) {
      (map[source] = map[source] || []).push(section);
    }
  }
  return map;
})();

/**
 * Every `company_type` a partner should carry once added to the section
 * `sectionType`. Materializes virtual expansions into real memberships: adding
 * to "gros_oeuvre" yields ["gros_oeuvre", "terrassement"] so the partner is a
 * concrete member of BOTH the Gros œuvre and Terrassement sections and each can
 * remove its own tag independently. Plain types return just [sectionType].
 */
export function materializeSectionTypes(sectionType: string): string[] {
  return [sectionType, ...(REVERSE_COMPANY_TYPE_EXPANSIONS[sectionType] || [])];
}

/**
 * Add `sectionType` (and its materialized expansions) to a partner's existing
 * `company_types`, de-duplicated. Used everywhere a partner is added to a
 * project section.
 */
export function addSectionToCompanyTypes(
  existing: string[] | undefined,
  sectionType: string,
): string[] {
  return Array.from(
    new Set([...(existing || []), ...materializeSectionTypes(sectionType)]),
  );
}
